import {InputType, PromptType, QuestionDefinition} from "./questions";
import {Draft} from "immer";
import {BaseAppState, HostLobby, ServerLobby} from "engage-common/types";

export type TriviaState = {
    readonly common:CommonState
    readonly extra:ExtraState
    readonly host:HostState
    readonly chat:ChatState
    readonly server:ServerState
}

export interface CommonState {
    readonly state: TriviaStatus
    readonly timeMark: number
    readonly pauseMark?: number
    readonly currentIndex: number
    readonly currentQuestion?: QuestionDefinition //Player doesn't get correct answers
    readonly currentHint?: number
    readonly totalQuestions: number
    readonly gameOptions: GameOptions
    readonly polls: number[] //indices of the questions that are poll questions
}

export interface ExtraState {
    //Delayed copy of what is in ServerState. This is more efficient. We copy it at the end of submit state.
    readonly aggregateResponses: readonly AggregateResponses[]
    readonly participantCount: number
    readonly leaderboard: readonly LeaderboardEntry[]
    readonly teamScores: {readonly [teamID:string]:UserScores}
}

export interface HostState {
    readonly trivia: readonly QuestionDefinition[]
    readonly dashboard: HostDashboard
    //This is copy of what's in ServerState. Copied once per second during submit to save on bandwidth/processing.
    readonly currentAggregation?: AggregateResponses
}

export type ChatState = {
    readonly entries: ChatEntry[]
}

//The ServerState doesn't have to be readonly since it doesn't get transmitted over the wire
// and therefore doesn't need to go through Immer to produce patches. We should be able to modify it directly.
export interface ServerState {
    //Copied to ExtraState at the end of submit state. Copied to HostState once per second during submit.
    readonly aggregateResponses: AggregateResponses[]
    readonly scores: {[userID: string]: UserScores}
    readonly questionResponses: ProcessedResponses[]
    readonly leaderboard: {[userID:string]: LeaderboardEntry}
    //Used to determine when we last did occasional processing
    readonly lastIntervalProcessTime: number
}

export interface UserState {
    readonly lastPosition?:number
    readonly position?:number
    readonly streak:number
    readonly longestStreak:number
    readonly scores:UserScores
    readonly responses: readonly ProcessedResponse[]
    readonly chatReadCount: number
}

export function DefaultAppState(trivia:QuestionDefinition[] = [],
                                askForNicknames:boolean = false,
                                hostName:string = ""):TriviaState {
    return {
        common: initialCommonState(trivia.length, askForNicknames, hostName),
        extra: {
            aggregateResponses: [],
            participantCount: 0,
            //Just the top 20, sorted with nicknames
            leaderboard:[],
            teamScores: {}
        },
        host: {
            trivia,
            dashboard: {
                mostCorrect: 0,
                mostCorrectUsers: [],
                longestStreak: 0,
                longestStreakUsers: [],
                fastestTime: 0,
                fastestTimeUsers: []
            }
        },
        chat: {
            entries: []
        },
        server: {
            aggregateResponses: [],
            scores: {},
            questionResponses: [],
            //Entire leaderboard holding data for each person
            leaderboard:{},
            lastIntervalProcessTime: 0,
        }
    };
}

export function DefaultUserState():UserState {
    return {
        streak: 0,
        longestStreak: 0,
        scores: {
            total: 0,
            individual: [],
            totals: []
        },
        responses: [],
        chatReadCount: 0
    };
}

export enum TriviaStatus {
    Pending = "pending",
    Starting = "starting",
    QuestionStart = "question_start", //Just the prompt is shown
    QuestionSubmit = "question_submit", //Submitting answers is open for players
    QuestionSummary = "question_summary", //Submissions done, choices are shown but not the answer
    QuestionAnswer = "question_answer", //The answer is revealed
    QuestionDetails = "question_details", //Details about the answer are now shown (explanation and sources)
    Complete = "complete",
}

//Helper array to for the game being active
export const TriviaRunning:TriviaStatus[] = [TriviaStatus.Starting, TriviaStatus.QuestionStart,
    TriviaStatus.QuestionSubmit, TriviaStatus.QuestionSummary, TriviaStatus.QuestionAnswer,
    TriviaStatus.QuestionDetails];

export const BOT_DIVIDER = 1;

export const TriviaStatusTime: {[key in TriviaStatus] : number} = {
    [TriviaStatus.Pending] : 0, // Wait for the host to start the game
    [TriviaStatus.Starting] : 25*1000 / BOT_DIVIDER,
    [TriviaStatus.QuestionStart] : 10*1000 / BOT_DIVIDER,
    [TriviaStatus.QuestionSubmit] : 0, // The question type will determine the duration of this step
    [TriviaStatus.QuestionSummary] : 8*1000 / BOT_DIVIDER,
    [TriviaStatus.QuestionAnswer] : 5*1000 / BOT_DIVIDER,
    [TriviaStatus.QuestionDetails] : 15*1000 / BOT_DIVIDER,
    [TriviaStatus.Complete] : 0 //Wait for the host to restart the game
};

export type AnswerType = number | number[] | string[];

export interface AggregateResponses {
    readonly optionCounts: {readonly [option:number]:number}
    readonly fastestResponse?: {nickname:string, timeElapsed:number}
}

interface ProcessedResponses {
    readonly userResponses:  {readonly [userID:string]:ProcessedResponse}
}

export interface ProcessedResponse {
    value: AnswerType
    timeElapsed: number
    score: number
    poll: boolean
}

export interface GameOptions {
    // No timers shown, the game doesn't progress on its own
    readonly selfTimed:boolean
    readonly streamUrl?: string
    readonly showLeaderboard: boolean
    readonly showChat: boolean
    readonly hostName?: string
    readonly backgroundMusic: boolean
    readonly soundEffects: boolean
    readonly introVideoRectangle: boolean
    readonly durationOverrides: {readonly [key in TriviaStatus]? : number}
}

export interface UserScores {
    total: number
    totals: readonly number[]
    individual: readonly number[]
}

export interface LeaderboardEntry {
    total: number
    totals: number[]
    position: number
    lastPosition?: number
    streak: number
    longestStreak: number
    userID: string
    nickname: string
    teamID?: string
}

export const LeaderboardLength = 30;

export type ChatEntry = {
    readonly userID: string
    readonly teamID?: string
    readonly nickname: string
    readonly text: string[]
    readonly time: number
    verified?: boolean
}

export type HostDashboard = {
    readonly mostCorrect: number
    readonly mostCorrectUsers: string[]
    readonly longestStreak: number
    readonly longestStreakUsers: string[]
    readonly fastestTime: number
    readonly fastestTimeUsers: string[]
}

export function initialCommonState(questions:number = -1,
                                   askForNicknames: boolean = false,
                                   hostName:string = ""):Draft<CommonState> {
    return {
        state: TriviaStatus.Pending,
        timeMark: 0,
        currentIndex: -1,
        totalQuestions: questions,
        pauseMark: undefined,
        currentQuestion: undefined,
        gameOptions: {
            selfTimed: false,
            streamUrl: "",
            hostName: hostName,
            showLeaderboard: askForNicknames,
            showChat: askForNicknames,
            backgroundMusic: true,
            soundEffects: true,
            introVideoRectangle: false,
            durationOverrides: {},
        },
        polls: []
    };
}

export interface QuestionResponse {
    questionIndex: number
    value: AnswerType
}

export const HintDuration = 10*1000 / BOT_DIVIDER;

export function triviaStateDuration(state?:CommonState) {
    if(state) {
        if(state.state in state.gameOptions.durationOverrides) {
            return state.gameOptions.durationOverrides[state.state]!;
        }
        return state.state === TriviaStatus.QuestionSubmit ? state.currentQuestion!.duration : TriviaStatusTime[state.state];
    } else {
        return 0;
    }
}



export function adjustScoreForTime(question:QuestionDefinition, baseScore:number, timeElapsed:number, durationOverride?:number) {
    if(question.promptType === PromptType.Hint) {
        const hintIndex = Math.floor(timeElapsed / HintDuration); //First hint is free (hence floor)
        baseScore = baseScore * (question.hints.length - hintIndex) / question.hints.length;
    } else {
        const gracePeriod = (durationOverride ?? question.duration) / 4;
        const penalizedTime = (durationOverride ?? question.duration) - gracePeriod;
        const elapsedTime = Math.max(0, timeElapsed - gracePeriod);
        if(question.inputType === InputType.Phrase) {
            baseScore = baseScore * ((penalizedTime - elapsedTime) / penalizedTime);
        } else {
            baseScore = baseScore * ((penalizedTime - elapsedTime) / (penalizedTime*2) + 0.5);
        }
    }
    return Math.round(baseScore);
}

//Type that represents the JSON file created by Download State and used by Upload State in the Host tool
export type TriviaGameState = {
    lobbyState: BaseAppState<TriviaState, UserState>
    hostLobby: HostLobby
    serverLobby: ServerLobby
}

export const Letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";